import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/contexts/AuthContext';
import SharePanel from '@/components/feature/SharePanel';

const navItems = [
  { path: '/', icon: 'ri-home-5-line', activeIcon: 'ri-home-5-fill', label: '首页' },
  { path: '/videos', icon: 'ri-play-circle-line', activeIcon: 'ri-play-circle-fill', label: '视频' },
  { path: '/community', icon: 'ri-team-line', activeIcon: 'ri-team-fill', label: '社区' },
  { path: '/shop', icon: 'ri-store-2-line', activeIcon: 'ri-store-2-fill', label: '商店' },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const { totalCount } = useCart();
  const { user } = useAuth();

  // Hide on auth page, dashboard pages
  const hiddenPaths = ['/login', '/teacher/dashboard', '/seller/dashboard'];
  if (hiddenPaths.some((p) => location.pathname.startsWith(p))) return null;

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[9990] bg-white border-t border-gray-100">
      {/* Safe area padding for iPhone notch */}
      <div className="flex items-center justify-around px-1 pt-2 pb-safe" style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-all cursor-pointer min-w-0"
            >
              <div className={`w-6 h-6 flex items-center justify-center transition-transform ${active ? 'scale-110' : ''}`}>
                <i className={`${active ? item.activeIcon : item.icon} text-xl ${active ? 'text-red-600' : 'text-gray-400'}`}></i>
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${active ? 'text-red-600' : 'text-gray-400'}`}>
                {item.label}
              </span>
              {active && (
                <span className="w-1 h-1 rounded-full bg-red-600 mt-0.5"></span>
              )}
            </Link>
          );
        })}

        {/* Cart */}
        <Link
          to="/cart"
          className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-all cursor-pointer relative"
        >
          <div className="w-6 h-6 flex items-center justify-center relative">
            <i className={`${isActive('/cart') ? 'ri-shopping-cart-fill' : 'ri-shopping-cart-line'} text-xl ${isActive('/cart') ? 'text-red-600' : 'text-gray-400'}`}></i>
            {totalCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
                {totalCount > 9 ? '9+' : totalCount}
              </span>
            )}
          </div>
          <span className={`text-xs font-medium whitespace-nowrap ${isActive('/cart') ? 'text-red-600' : 'text-gray-400'}`}>
            购物车
          </span>
          {isActive('/cart') && <span className="w-1 h-1 rounded-full bg-red-600 mt-0.5"></span>}
        </Link>

        {/* Profile / Login */}
        <Link
          to={user ? '/profile' : '/login'}
          className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-all cursor-pointer"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            {user ? (
              <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
                <span className="text-white font-bold text-xs">
                  {(user.email?.[0] ?? 'U').toUpperCase()}
                </span>
              </div>
            ) : (
              <i className={`${isActive('/login') ? 'ri-user-fill' : 'ri-user-line'} text-xl ${isActive('/login') ? 'text-red-600' : 'text-gray-400'}`}></i>
            )}
          </div>
          <span className={`text-xs font-medium whitespace-nowrap ${isActive('/login') || isActive('/profile') ? 'text-red-600' : 'text-gray-400'}`}>
            {user ? '我的' : '登录'}
          </span>
          {(isActive('/login') || isActive('/profile')) && (
            <span className="w-1 h-1 rounded-full bg-red-600 mt-0.5"></span>
          )}
        </Link>
      </div>
    </nav>
  );
}
